import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SectionTitle from '../../../components/common/SectionTitle/SectionTitle';
import Button from '../../../components/common/Button/Button';
import DataTable from '../../../components/common/DataTable/DataTable';
import EmptyState from '../../../components/common/EmptyState/EmptyState';
import Loading from '../../../components/common/Loading/Loading';
import FilterBar from '../../../components/common/FilterBar/FilterBar';
import FilterGroup from '../../../components/common/FilterGroup/FilterGroup';
import MultiSelectFilter from '../../../components/common/MultiSelectFilter/MultiSelectFilter';
import SingleSelectFilter from '../../../components/common/SingleSelectFilter/SingleSelectFilter';
import ClearFiltersButton from '../../../components/common/ClearFiltersButton/ClearFiltersButton';
import RecordCount from '../../../components/common/RecordCount/RecordCount';
import PageSizeSelect from '../../../components/common/PageSizeSelect/PageSizeSelect';
import Pagination from '../../../components/common/Pagination/Pagination';
import { queryFilter, useQueryFilters } from '../../../hooks/useQueryFilters';
import { useAuth } from '../../../contexts/AuthContext';
import { canAccessFullAdministration } from '../../../utils/permissions';
import {
  EQUIPMENT_CATEGORY_LABELS,
  EQUIPMENT_CATEGORY_ORDER,
  type EquipmentCategory,
} from '../../../constants/equipmentCategory';
import {
  EquipmentCatalogService,
  type EquipmentCatalogPayload,
  type EquipmentCatalogRow,
} from '../../../services';
import type { PaginationInfo } from '../../../types';
import EquipmentModal from '../modals/EquipmentModal';
import styles from './EquipamentosTab.module.css';

const GRID_TEMPLATE = '56px 2fr 1.2fr 90px 56px';
const HEADERS = ['', 'Nome', 'Categoria', 'Ativo', ''];
const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const DEFAULT_ACTIVE_FILTER = 'true';

const INITIAL_PAGINATION: PaginationInfo = {
  page: 1,
  pageSize: 20,
  totalRecords: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

const ACTIVE_FILTER_OPTIONS = [
  { value: 'true', label: 'Ativo' },
  { value: 'false', label: 'Inativo' },
  { value: 'all', label: 'Todos' },
];

const CATEGORY_FILTER_OPTIONS = EQUIPMENT_CATEGORY_ORDER.map((category) => ({
  value: category,
  label: EQUIPMENT_CATEGORY_LABELS[category],
}));

const FILTER_SCHEMA = {
  page: queryFilter.integer(1, { min: 1, param: 'equipPage' }),
  pageSize: queryFilter.integer(20, { allowed: PAGE_SIZE_OPTIONS, param: 'equipPageSize' }),
  isActive: queryFilter.enum(DEFAULT_ACTIVE_FILTER, ['true', 'false', 'all'], { param: 'equipIsActive' }),
  categories: queryFilter.array<string>([], { param: 'equipCategories' }),
};

const resolveActiveFilterValues = (value: string): boolean[] | undefined => {
  if (value === 'false') {
    return [false];
  }
  if (value === 'all') {
    return undefined;
  }
  return [true];
};

const EquipamentosTab: React.FC = () => {
  const { roles } = useAuth();
  const canManage = canAccessFullAdministration(roles);
  const { filters, setFilter, setFilters } = useQueryFilters(FILTER_SCHEMA, { pageKey: 'page' });
  const {
    page,
    pageSize,
    isActive: filterIsActive,
    categories: filterCategories,
  } = filters;

  const [items, setItems] = useState<EquipmentCatalogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EquipmentCatalogRow | null>(null);
  const [saving, setSaving] = useState(false);

  const hasActiveFilters = useMemo(() => (
    filterIsActive !== DEFAULT_ACTIVE_FILTER
    || filterCategories.length > 0
  ), [filterCategories, filterIsActive]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const categories = filterCategories.filter(
        (value): value is EquipmentCategory => (
          EQUIPMENT_CATEGORY_ORDER.includes(value as EquipmentCategory)
        ),
      );

      const rows = await EquipmentCatalogService.list({
        categories: categories.length > 0 ? categories : undefined,
        isActive: resolveActiveFilterValues(filterIsActive),
      });

      setItems(rows);
    } catch {
      setError('Erro ao carregar equipamentos.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filterCategories, filterIsActive]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  const pagination = useMemo((): PaginationInfo => {
    const totalRecords = items.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    const safePage = Math.min(page, totalPages);

    return {
      page: safePage,
      pageSize,
      totalRecords,
      totalPages,
      hasNextPage: safePage < totalPages,
      hasPreviousPage: safePage > 1,
    };
  }, [items.length, page, pageSize]);

  const pageItems = useMemo(() => {
    const start = (pagination.page - 1) * pagination.pageSize;
    return items.slice(start, start + pagination.pageSize);
  }, [items, pagination.page, pagination.pageSize]);

  const handleClearFilters = useCallback(() => {
    setFilters({
      isActive: DEFAULT_ACTIVE_FILTER,
      categories: [],
    });
  }, [setFilters]);

  const handleOpenCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (equipment: EquipmentCatalogRow) => {
    setEditing(equipment);
    setModalOpen(true);
  };

  const handleCancel = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleDeactivate = async (equipment: EquipmentCatalogRow) => {
    setSaving(true);
    try {
      await EquipmentCatalogService.setActive(equipment.id, false);
      setModalOpen(false);
      setEditing(null);
      await loadItems();
    } catch {
      throw new Error('Erro ao desativar equipamento.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (
    payload: EquipmentCatalogPayload,
    pendingFiles: File[],
  ) => {
    setSaving(true);
    try {
      if (editing) {
        await EquipmentCatalogService.update(editing.id, payload);
      } else {
        await EquipmentCatalogService.create(payload, pendingFiles);
      }
      setModalOpen(false);
      setEditing(null);
      await loadItems();
    } catch (saveError) {
      throw saveError instanceof Error
        ? saveError
        : new Error('Erro ao salvar equipamento.');
    } finally {
      setSaving(false);
    }
  };

  if (error && items.length === 0 && !loading) {
    return <p className={styles.error}>{error}</p>;
  }

  return (
    <>
      <div className={styles.sectionHeader}>
        <SectionTitle as="h2" text="Equipamentos" />
        {canManage && (
          <Button variant="primary" size="small" onClick={handleOpenCreate}>
            Novo equipamento
          </Button>
        )}
      </div>

      <FilterBar gap="sm" pushFirstChild>
        <RecordCount
          count={`${pagination.totalRecords} ${pagination.totalRecords === 1 ? 'equipamento' : 'equipamentos'}`}
          className={styles['record-count']}
        />
        <FilterGroup>
          <SingleSelectFilter
            value={filterIsActive}
            onChange={(value) => setFilter('isActive', value as 'true' | 'false' | 'all')}
            options={ACTIVE_FILTER_OPTIONS}
            placeholder="Status"
          />
          <MultiSelectFilter
            values={filterCategories}
            onChange={(values) => setFilter('categories', values)}
            options={CATEGORY_FILTER_OPTIONS}
            placeholder="Categoria"
            hideChips
          />
        </FilterGroup>
        {hasActiveFilters && (
          <ClearFiltersButton onClick={handleClearFilters} />
        )}
      </FilterBar>

      {loading ? (
        <Loading />
      ) : pageItems.length === 0 ? (
        <EmptyState icon="fitness_center" message="Nenhum equipamento encontrado." />
      ) : (
        <>
          <DataTable headers={HEADERS} gridTemplate={GRID_TEMPLATE} borderedRows boldHeaders>
            {pageItems.map((equipment) => (
              <DataTable.Row key={equipment.id}>
                <span className={styles.thumbCell}>
                  {equipment.thumbnailUrl ? (
                    <img
                      src={equipment.thumbnailUrl}
                      alt=""
                      className={styles.thumb}
                    />
                  ) : (
                    <span className={styles.thumbPlaceholder}>
                      <span className="material-symbols-outlined">image</span>
                    </span>
                  )}
                </span>
                <span className={styles.colName}>{equipment.name}</span>
                <span>{EQUIPMENT_CATEGORY_LABELS[equipment.category]}</span>
                <span>
                  <span className={equipment.isActive ? styles.badgeActive : styles.badgeInactive}>
                    {equipment.isActive ? 'Ativo' : 'Inativo'}
                  </span>
                </span>
                <span className={styles.actions}>
                  {canManage && (
                    <button
                      className={styles.actionBtn}
                      onClick={() => handleOpenEdit(equipment)}
                      aria-label="Editar equipamento"
                      type="button"
                    >
                      <span className="material-symbols-outlined">edit</span>
                    </button>
                  )}
                </span>
              </DataTable.Row>
            ))}
          </DataTable>

          <div className={styles.bottomRow}>
            <PageSizeSelect
              value={pageSize}
              options={PAGE_SIZE_OPTIONS}
              onChange={(event) => setFilter('pageSize', Number.parseInt(event.target.value, 10))}
            />
            <div className={styles.paginationCenter}>
              <Pagination
                pagination={pagination.totalRecords === 0 ? INITIAL_PAGINATION : pagination}
                loading={loading}
                onPageChange={(targetPage) => setFilter('page', targetPage, { resetPage: false })}
              />
            </div>
          </div>
        </>
      )}

      <EquipmentModal
        open={modalOpen}
        equipment={editing}
        loading={saving}
        canManage={canManage}
        onConfirm={handleSave}
        onDeactivate={handleDeactivate}
        onImagesChanged={() => {
          void loadItems();
        }}
        onCancel={handleCancel}
      />
    </>
  );
};

export default EquipamentosTab;
